/**
 * feat-259 — the ONE footer, and the ONE error banner, for all five curriculum
 * item detail panels.
 *
 * Design authority: `design/footer.html` (the container line and the Delete
 * slot), `design/error-states.html` (the banner's seven states and the copy
 * formula). `design/NOTES.md` §2 and §10 are the ship-time checklist.
 *
 * LAYOUT CONTRACT — feat-231, do not weaken:
 *   <aside className="… flex flex-col min-h-0 overflow-hidden">
 *     <div className="flex-1 min-h-0 overflow-y-auto …">…fields…</div>
 *     <SaveErrorBanner />   ← shrink-0
 *     <ItemDetailFooter />  ← shrink-0
 *   </aside>
 * Both the banner and the footer are `shrink-0` SIBLINGS of the scroll area.
 * Growth therefore eats the field list and never the footer, so Delete and the
 * save status stay put at any banner height — and a persistent error cannot be
 * scrolled out of sight. Putting either inside the scroll area to "save
 * vertical space" is the feat-231 squeeze wearing a different hat.
 */

import React from 'react';
import { SaveStatus } from './SaveStatus';
import { AUTO_SAVE_COPY } from './autoSaveCopy';
import type { AutoSaveError, AutoSaveStatus } from './useAutoSave';

/** footer.html §"The markup, once". `shrink-0` is load-bearing. */
export const ITEM_FOOTER_CLASSES =
  'border-t border-softBorder bg-white px-5 py-3 shrink-0 flex items-center justify-between gap-3';

/** NOTES §2. The hairline is the one change to Delete itself: with Save and
 *  Cancel gone the footer is two pieces of coloured text and nothing in the
 *  SHAPE says which one is clickable — while the clickable one is the
 *  destructive one. Bordered = button, borderless = report. Delete's COLOUR
 *  emphasis is deliberately not raised; making the sole remaining button the
 *  loudest object in the panel is the wrong trade in a panel whose premise is
 *  that editing is now cheap. `px-2` → `px-3` only so the ring doesn't crowd
 *  the glyphs. */
export const ITEM_FOOTER_DELETE_CLASSES =
  'text-danger-text text-sm font-semibold px-3 py-2 rounded-full border border-softBorder ' +
  'hover:bg-danger-surface hover:border-danger-line/40';

export interface ItemDetailFooterProps {
  /** Absent in create mode → the left slot renders `<span />`, exactly as today.
   *  Do NOT substitute a disabled Delete: an always-rendered-but-disabled
   *  control is the feat-255/258 pattern for a GATED capability, and this one is
   *  ABSENT — there is nothing to delete until the first blur creates the row. */
  onDelete?: () => void;
  status: AutoSaveStatus;
  savedAt: number | null;
  /** The rehearsal editor's strip overrides this: it keeps `sticky bottom-0` and
   *  its `bg-purple-50` (feat-231's mechanism constraint, NOTES §4). Every other
   *  panel leaves it alone. */
  containerClassName?: string;
}

export const ItemDetailFooter: React.FC<ItemDetailFooterProps> = ({
  onDelete,
  status,
  savedAt,
  containerClassName = ITEM_FOOTER_CLASSES,
}) => (
  <div className={containerClassName} data-testid="item-detail-footer">
    {onDelete ? (
      <button type="button" onClick={onDelete} className={ITEM_FOOTER_DELETE_CLASSES}>
        {AUTO_SAVE_COPY.delete}
      </button>
    ) : (
      <span />
    )}
    <SaveStatus status={status} savedAt={savedAt} />
  </div>
);

export interface SaveErrorBannerProps {
  error: AutoSaveError<Record<string, unknown>> | null;
  /** Why a write is being withheld — a create-blocker, or an upload in flight. */
  blockedReason?: string | null;
  /** The second sentence for a create-blocker: state G's "Nothing has been saved
   *  yet — your text is still here." Omitted when the row already exists. */
  blockedDetail?: string | null;
  /** `attention` (default) when the editor can fix it from THIS panel — pick a
   *  role. `danger` when they cannot: no role has an active prompt template
   *  version is a Prompt Management setup task, not something this panel can
   *  offer. design/NOTES.md §6a splits the three role conditions this way. */
  blockedTone?: 'attention' | 'danger';
  /** True while the parent is HOLDING a selection change on this panel (AT9).
   *  The banner already on screen escalates in place. */
  pendingNavigation?: boolean;
  /** QUIZ_TERMS.<type>.singular — only used for the create-failed lede. */
  typeSingular: string;
  onRetry: () => void;
  onLeaveAnyway?: () => void;
}

/** One banner, never a stack. */
export const SaveErrorBanner: React.FC<SaveErrorBannerProps> = ({
  error,
  blockedReason = null,
  blockedDetail = null,
  blockedTone = 'attention',
  pendingNavigation = false,
  typeSingular,
  onRetry,
  onLeaveAnyway,
}) => {
  // feat-259 — a withheld write explains itself as soon as it is withheld, not
  // only when navigation is held. Gating this on `pendingNavigation` left a
  // create the app was refusing (no role picked, an upload still running) with
  // NO feedback at all until the editor tried to leave — and once `role` came
  // out of the rehearsal advisory (design/NOTES.md §6c) that silence was total.
  if (!error && !blockedReason) return null;

  const labels = error?.fieldLabels ?? [];
  const named = labels.length > 0 ? labels.join(', ') : typeSingular;

  // `attention-*` = "you authored this, and you can fix it"; `danger-*` is
  // reserved for a fault the author CANNOT fix from inside the panel
  // (TOKENS §1o, feat-242). Neither is `warning-*`, which cannot carry text at
  // all — `warning-on` on `warning-surface` is 1.999:1, an AA failure by a
  // factor of 2.25 (feat-248).
  // A withheld write with no error is the author's to resolve, so it takes the
  // `attention-*` family too — unless the panel says otherwise.
  const attention = error?.kind === 'validation' || (!error && blockedTone === 'attention');
  const skin = attention
    ? 'border-attention-line/50 bg-attention-surface text-attention-on'
    : 'border-danger-line/40 bg-danger-surface text-danger-text';

  const lede = () => {
    if (pendingNavigation) {
      return error ? AUTO_SAVE_COPY.stillUnsaved(named) : blockedReason;
    }
    if (!error) return blockedReason;
    if (error.kind === 'validation') return error.message;
    if (error.kind === 'auth') return AUTO_SAVE_COPY.signInExpired(named);
    if (error.kind === 'create') return AUTO_SAVE_COPY.couldNotCreate(typeSingular);
    return labels.length > 1
      ? AUTO_SAVE_COPY.couldNotSaveFields(labels.length)
      : AUTO_SAVE_COPY.couldNotSaveField(named);
  };

  return (
    <div
      role="alert"
      className={`shrink-0 border-t px-5 py-2.5 text-[11.5px] leading-snug ${skin}`}
      data-testid="save-error-banner"
    >
      <strong className="font-bold">{lede()}</strong>{' '}
      {pendingNavigation ? (
        <>
          {!error && blockedDetail ? <>{blockedDetail} </> : null}
          {AUTO_SAVE_COPY.wouldLeaveBehind}
          <span className="flex items-center gap-3 mt-2">
            {/* No Try again when the write was never ATTEMPTED. A create the app
                is withholding — no role picked, an upload still running — cannot
                be cleared by retrying it, and offering the button would be the
                same lie state E rules out. The escape hatch is still there. */}
            {error && error.retryable !== false && (
              <button
                type="button"
                onClick={onRetry}
                className="bg-danger-text text-white font-semibold rounded-full px-3.5 py-1.5 text-[11.5px] hover:brightness-110"
              >
                {AUTO_SAVE_COPY.tryAgain}
              </button>
            )}
            {/* AT9's escape hatch. Without it a permanently-failing save (an
                expired session, an unfixable 422) leaves a page reload as the
                only exit — and a reload discards the change SILENTLY, which is
                exactly what AT8's "never silently reverted" forbids. A hard hold
                defeats its own purpose. Spec §"When a save fails", dated
                decision 2026-08-24. */}
            <button
              type="button"
              onClick={onLeaveAnyway}
              className="text-inkMuted font-semibold underline"
            >
              {AUTO_SAVE_COPY.leaveAnyway}
            </button>
          </span>
        </>
      ) : (
        <>
          {!error && blockedDetail ? <>{blockedDetail} </> : null}
          {labels.length > 1 && error?.kind !== 'validation' && <>{AUTO_SAVE_COPY.fieldList(labels)} </>}
          {error?.kind === 'create' ? AUTO_SAVE_COPY.createNothingSaved : null}
          {error && error.kind !== 'create' && error.kind !== 'validation' ? AUTO_SAVE_COPY.valueKept : null}{' '}
          {error?.kind === 'auth' && <>{AUTO_SAVE_COPY.signInAgainThen} </>}
          {/* No Try again on the author-fixable variant: retrying is not the fix
              and offering it would be a lie. The write was never sent. */}
          {error && error.retryable && (
            <button type="button" onClick={onRetry} className="underline font-semibold">
              {AUTO_SAVE_COPY.tryAgain}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default ItemDetailFooter;
